import { DebugService } from './debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../constants/app.constants.js';
import { ApiService } from '../../core/services/api/api.service.js';
import { ApplicationService } from '../../services/application.service.js';
import { SessionService } from '../../core/services/session/session.service.js';

export class _PolicyLimitsService {
  constructor() {
    this.debug = new DebugService('PolicyLimitsService');
    this.allPolicyLimitsResponse = null;
  }

  initialize() {
    this.fetchAllPolicyLimits()
      .then((allPolicyLimitsResponse) => {
        this.debug.log('Policy limits initialized! ');
        this.allPolicyLimitsResponse = allPolicyLimitsResponse;
      })
      .catch(() => {
        this.debug.error('Error initializing policy limits');
        this.allPolicyLimitsResponse = null;
      });
  }

  async fetchAllPolicyLimits() {
    const url = REFI_VEHICLE_LOAN_CONST.GET_POLICY_LIMITS_URL();
    try {
      if (this.allPolicyLimitsResponse) {
        this.debug.log('returning cached policy limits');
        return this.allPolicyLimitsResponse;
      }
      const policyLimitsResponse = await ApiService.get(url);
      return policyLimitsResponse;
    } catch (error) {
      this.debug.error('error getting all policy limits', error);
      this.allPolicyLimitsResponse = null;
      return null;
    }
  }

  checkRequiredParams(param, paramName) {
    if (param === null || param === undefined) {
      this.debug.error(`${paramName} is required`);
      return false;
    }
    return true;
  }

  getPolicyLimit(policyName, fallbackValue) {
    if (!this.checkRequiredParams(policyName, 'Policy name')) return null;
    if (!this.checkRequiredParams(fallbackValue, 'Fallback value')) return null;

    const productType = this.getProductType();
    const allPolicyLimits = this.allPolicyLimitsResponse;

    if (allPolicyLimits === null) {
      this.debug.error('No policy limits found');
      return null;
    }

    const policyLimits = allPolicyLimits?.policyLimits?.find(
      (policyLimit) => policyLimit.productType === productType
    );

    if (!policyLimits) {
      this.debug.error(
        'No policy limits found for product type: ',
        productType
      );
      return null;
    }

    const policyLimit = policyLimits?.policyLimitValues.find(
      (_policyLimit) => _policyLimit.name === policyName
    );

    return policyLimit ?? null;
  }

  getIntPolicyValue(policyName, fallbackValue) {
    if (!this.checkRequiredParams(policyName, 'Policy name')) return null;
    if (!this.checkRequiredParams(fallbackValue, 'Fallback value')) return null;

    const policyLimit = this.getPolicyLimit(policyName, fallbackValue);
    const value = policyLimit?.value ?? fallbackValue;
    const parsedValue = value !== null ? parseInt(value, 10) : null;

    if (Number.isNaN(parsedValue)) {
      this.debug.error(
        `Policy name: ${policyName} value is not a number: ${value}`
      );
      return fallbackValue;
    }

    return parsedValue;
  }

  getMaxLtvValue(fallbackValue) {
    if (!this.checkRequiredParams(fallbackValue, 'Fallback value')) return null;

    const policyLimit = this.getPolicyLimit('MAX_LOAN_TO_VALUE', fallbackValue);
    if (!policyLimit || !policyLimit.value) {
      return fallbackValue;
    }
    const value = policyLimit?.value ?? fallbackValue;

    const maxLtvValue = value !== null ? parseInt(value, 10) : null;

    if (Number.isNaN(maxLtvValue)) {
      this.debug.error(
        'Policy name MAX_LOAN_TO_VALUE value is not a number: ',
        value
      );
      return fallbackValue;
    }

    if (maxLtvValue === null) {
      return fallbackValue;
    }
    return maxLtvValue / 100;
  }

  getPaprMinimumValue(fallbackValue) {
    if (!this.checkRequiredParams(fallbackValue, 'Fallback value')) return null;
    const policyLimit = this.getPolicyLimit('APR_FLOOR', fallbackValue);
    const value = policyLimit?.value ?? fallbackValue;
    const parsedValue = value !== null ? parseFloat(value) : null;
    return Number.isNaN(parsedValue) ? fallbackValue : parsedValue;
  }

  getProductType() {
    const offerTypeCode = ApplicationService.MarketingOffer?.offerTypeCode;
    const promotionTypeCode = SessionService.getPromotion()?.offerTypeCode;

    if (
      offerTypeCode === REFI_VEHICLE_LOAN_CONST.OFFER_TYPE_CODES.preApproval ||
      promotionTypeCode === REFI_VEHICLE_LOAN_CONST.PROMOTION_TYPES.PRE_APPROVAL
    ) {
      return REFI_VEHICLE_LOAN_CONST.PRODUCT_TYPE_CODES.PRE_APPROVED;
    }

    if (
      offerTypeCode ===
        REFI_VEHICLE_LOAN_CONST.OFFER_TYPE_CODES.guaranteedRate ||
      promotionTypeCode ===
        REFI_VEHICLE_LOAN_CONST.PROMOTION_TYPES.GUARANTEED_RATE
    ) {
      return REFI_VEHICLE_LOAN_CONST.PRODUCT_TYPE_CODES.GUARANTEED_RATE;
    }

    return REFI_VEHICLE_LOAN_CONST.PRODUCT_TYPE_CODES.BAU;
  }
}
export const PolicyLimitsService = new _PolicyLimitsService();

export const POLICY_LIMITS_CONST = {
  MAX_LTV: () => PolicyLimitsService.getMaxLtvValue(1.6),
  MAX_MONTHLY_PAYMENT: () =>
    PolicyLimitsService.getIntPolicyValue('MAX_MONTHLY_PAYMENT', 1200),
  MIN_PAYOFF_AMOUNT: () =>
    PolicyLimitsService.getIntPolicyValue('MIN_FINANCED_AMOUNT', 7500),
  MAX_PAYOFF_AMOUNT: () =>
    PolicyLimitsService.getIntPolicyValue('MAX_FINANCED_AMOUNT', 75000),
  MIN_REMAINING_TERM: () =>
    PolicyLimitsService.getIntPolicyValue('MIN_TERM', 18),
  MAX_REMAINING_TERM: () =>
    PolicyLimitsService.getIntPolicyValue('MAX_TERM', 99),
  PAPR_MINIMUM: () => PolicyLimitsService.getPaprMinimumValue(0.0),
  MAX_VEHICLE_AGE: () =>
    PolicyLimitsService.getIntPolicyValue('MAX_VEHICLE_AGE', 10),
};
